import { useEffect, useState } from 'react';

export type ToastType = 'info' | 'error' | 'success' | 'warning';

export interface ToastMessage {
  id?: string;
  type?: ToastType;
  title?: string;
  text?: string;
  removeTime?: number;
}

export const EventType = {
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  REMOVE_MESSAGE: 'REMOVE_MESSAGE',
} as const;

export type EventTypeValue = (typeof EventType)[keyof typeof EventType];

export function dispatchMessage(eventType: EventTypeValue, message: ToastMessage): void {
  const event = new CustomEvent(eventType, { detail: message });
  document.dispatchEvent(event);
}

function isCustomEvent(event: Event): event is CustomEvent {
  return 'detail' in event;
}

function getMessageFromEvent(e: Event): ToastMessage | undefined {
  if (isCustomEvent(e)) {
    return e.detail as ToastMessage;
  }
  return undefined;
}

function useToasts(): { messages: Map<ToastMessage['id'], ToastMessage> } {
  const [messages, setMessages] = useState<Map<ToastMessage['id'], ToastMessage>>(new Map());

  useEffect(() => {
    document.addEventListener(EventType.ADD_MESSAGE, onAddMessage);
    document.addEventListener(EventType.UPDATE_MESSAGE, onAddMessage);
    document.addEventListener(EventType.REMOVE_MESSAGE, onRemoveMessage);

    return () => {
      document.removeEventListener(EventType.ADD_MESSAGE, onAddMessage);
      document.removeEventListener(EventType.UPDATE_MESSAGE, onAddMessage);
      document.removeEventListener(EventType.REMOVE_MESSAGE, onRemoveMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  function onAddMessage(e: Event) {
    const message = getMessageFromEvent(e);

    if (message) {
      const updatedMessages = new Map(messages);
      updatedMessages.set(message.id, message);

      if (message.removeTime) {
        setTimeout(() => {
          removeMessage(message);
        }, message.removeTime);
      }

      setMessages(updatedMessages);
    }
  }

  function onRemoveMessage(e: Event) {
    const message = getMessageFromEvent(e);

    if (message) {
      removeMessage(message);
    }
  }

  function removeMessage(message: ToastMessage) {
    const updatedMessages = new Map(messages);
    updatedMessages.delete(message.id);

    setMessages(updatedMessages);
  }

  return { messages };
}

export default useToasts;
